import { Trash, Warning } from '@phosphor-icons/react';
import classNames from 'classnames';
import type { ReactElement, Ref } from 'react';
import type { ComposerElement, PreviewMode } from '../../../../../../../types/pageEditor';
import { isNullOrEmpty } from '../../../../../../../utils/guard';
import ButtonIcon from '../../../../../../buttons/ButtonIcon';
import { isComponentConfigured } from '../../../../../utils';
import { usePageEditor } from '../../../../PageEditorProvider';
import { useComposer } from '../../ComposerProvider';

interface Props {
    id: string;
    parentId: string | null | undefined;
    classes: string;
    drag: Ref<HTMLDivElement> | null;
    children: ReactElement<{ config: { componentType: string; parentId: string | null } }>[];
    config: ComposerElement;
    preview: PreviewMode | null;
}

const Chart = ({ id, parentId, classes, drag, children, config, preview }: Props) => {
    const { developerName, label } = config;

    const { setElementToDelete, dragDropElements } = useComposer();
    const { getComponent } = usePageEditor();

    const validated =
        !isNullOrEmpty(developerName) &&
        dragDropElements
            .filter((element) => element.parentId === id)
            .every((config) =>
                isComponentConfigured(
                    {
                        ...config,
                        componentType: config.componentType?.toUpperCase() as string,
                    },
                    getComponent,
                ),
            );

    const renderDataSet = () => {
        const { renderFn } = getComponent(children[0].props?.config.componentType);

        const parentElement = dragDropElements.find(
            (element) => element.id === children[0].props?.config.parentId,
        );

        return renderFn({
            componentType: children[0].props.config.componentType,
            isStacked:
                parentElement && parentElement.attributes !== null
                    ? parentElement.attributes['$isStacked'] === 'true'
                    : false,
        });
    };

    const headerClasses = classNames({
        'page-container-header': true,
        draggable: true,
        preview: !!preview,
    });

    const bodyClasses = classNames(classes, 'component-preview-chart');

    const componentTitle = developerName ? `Chart: ${developerName}` : 'New Chart';

    const isDraggable = !preview;

    return (
        // biome-ignore lint/a11y/useSemanticElements: Requires dedicated refactor to use correct element
        <div
            id={id}
            role="button"
            tabIndex={-1}
            className="page-container-inner"
            ref={isDraggable ? drag : null}
        >
            {parentId ? (
                <div className={headerClasses}>
                    <div title={componentTitle} className="page-element-title">
                        {componentTitle}
                    </div>
                    <div className="container-header-actions flex-child-right">
                        <ButtonIcon
                            className="header-icon-button"
                            title="Delete Chart"
                            onClick={() => setElementToDelete({ targetId: id })}
                        >
                            <Trash size={20} weight="fill" />
                        </ButtonIcon>
                    </div>
                </div>
            ) : null}
            <div className={bodyClasses}>
                {label ? <span className="page-component-preview-label">{label}</span> : null}
                {children.length === 0 ? 'Your chart must contain a dataset' : renderDataSet()}
            </div>
            {!validated && (
                <mark className="config-warning flex-child-right" data-testid="mark">
                    <Warning weight="fill" />
                </mark>
            )}
        </div>
    );
};
export default Chart;
